import { ReactNode } from 'react';
import { AccountBillingView } from '../../api/platform';
import { RowCard } from '../../components/fs';
import { pendingPriceOf } from './ChangePlanDialog';
import {
  TrialRow, convertPrice, monDY, percentRunning, percentWords, perShort, periodEndsSubscription, restorePoint,
  trialPlanOf,
} from './accountBillingModals';
import { localDay, money } from './planDisplay';

/**
 * The Plan & pricing card, shared by the account page's Overview (screen 02)
 * and its Billing tab (screen 04: "the same rows as Overview, plus a Billing
 * email row"). On the Overview the rows only say; on the Billing tab they
 * also carry the actions that stayed out of the Change plan dialog, through
 * `billing` — without it the rows render exactly as the Overview always has.
 */

/** What they pay each period once a running percent comes off — the bill's own figure. */
export function paysCents(view: AccountBillingView): number | null {
  const { account, price } = view;
  if (price.cents === null) return null;
  const pct = percentRunning(account) && account.price_override_cents === null ? account.discount_percent : null;
  return pct ? Math.round((price.cents * (100 - pct)) / 100) : price.cents;
}

/** A card with the design's head: a 15px title and, on the right, its link or aside. */
export function Card({ title, aside, children, className }: {
  title: string; aside?: ReactNode; children: ReactNode; className?: string;
}) {
  return (
    <section className={`fs-card pl-ao-card${className ? ` ${className}` : ''}`}>
      <div className="st-card-head pl-ao-card-head">
        <h2>{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** One label / value / sub-line row of the Plan & pricing card, with the Billing tab's actions and detail. */
export function Row({ label, children, sub, tone, actions, extra }: {
  label: ReactNode; children: ReactNode; sub?: ReactNode; tone?: 'muted' | 'info';
  actions?: ReactNode; extra?: ReactNode;
}) {
  return (
    <RowCard label={label} actions={actions || undefined}>
      <div className={`pl-ao-value${tone ? ` ${tone}` : ''}`}>{children}</div>
      {sub && <div className="pl-ao-rowsub">{sub}</div>}
      {extra}
    </RowCard>
  );
}

/** The Billing tab's additions to the shared rows. */
export interface BillingRowExtras {
  /** the Plan row's sub-line, in place of "Billed monthly" */
  planSub?: ReactNode;
  /** rows between Plan and Pricing: what is scheduled */
  afterPlan?: ReactNode;
  pricingActions?: ReactNode;
  pricingExtra?: ReactNode;
  freeActions?: ReactNode;
  freeExtra?: ReactNode;
  /** rows after They pay: the billing email and the Portal */
  after?: ReactNode;
}

/** "15% off for 12 months", "20% off until Mar 1, 2027", "10% off, forever". */
const pctWords = (pct: number, months: number | null, until: string | null): string =>
  (!months && until ? `${pct}% off until ${monDY(until)}` : percentWords(pct, months));

/** Plan · Pricing · Free period · They pay (screens 02 and 04), said as the server behaves. */
export function PlanPricingRows({ view, deleteAfter, billing }: {
  view: AccountBillingView;
  /** the instant the account is deleted, while it waits */
  deleteAfter: string | null | undefined;
  billing?: BillingRowExtras;
}) {
  const { plan, price, mrr } = view;
  const acct = view.account;
  const trial = view.trial as TrialRow | null;
  const deleting = !!deleteAfter;
  // A free period that converts into terms set in Change plan (076): those are
  // what its first charge carries, so the rows describe them (screen 04's
  // Kestrel: "15% off for 12 months", "$109.65/mo from Oct 31").
  const pending = trial?.auto_convert && !deleting ? pendingPriceOf(view) : null;
  const pp = pending?.price;
  const interval = pending ? pending.interval : price.interval;
  const unit = perShort(interval);
  const cur = pending ? pending.currency : plan.currency;
  const list = pending ? pending.listCents : price.listCents;
  const pct = pending ? (pp?.mode === 'discount' ? pp.percent : null) : percentRunning(acct) ? acct.discount_percent : null;
  const pctMonths = pending
    ? (pp?.mode === 'discount' && pp.duration !== 'forever' && 'months' in pp.duration ? pp.duration.months : null)
    : acct.discount_months;
  const pctEnds = pending ? pending.discountEnds : acct.discount_ends_at;
  const agreedCents = pending ? (pp?.mode === 'agreed' ? pp.cents : null) : acct.price_override_cents;
  const agreedNote = pending ? (pp?.mode === 'agreed' ? pp.note : null) : acct.price_override_note;
  const agreed = agreedCents !== null;
  const pays = paysCents(view);

  let pricing: ReactNode = 'List price';
  let pricingSub: ReactNode = null;
  if (agreed) {
    pricing = `Agreed ${money(agreedCents, cur)}${unit}${agreedNote ? ` · ${agreedNote}` : ''}`;
    pricingSub = list !== null ? `Instead of ${money(list, cur)}${unit} list` : null;
  } else if (pct) {
    const until = !pctMonths && pctEnds ? pctEnds : null;
    pricing = pctWords(pct, pctMonths, until);
    pricingSub = [
      pctEnds && !until ? `Ends ${monDY(pctEnds)}` : '',
      list !== null ? `then ${money(list, cur)}${unit}` : '',
    ].filter(Boolean).join(' · ');
    pricingSub = pricingSub ? String(pricingSub).charAt(0).toUpperCase() + String(pricingSub).slice(1) : null;
  }

  let freeSub: ReactNode = null;
  if (trial) {
    const then = deleting ? 'Ends with the deletion'
      : trial.auto_convert ? `Then converts to paid${pending && pending.planId !== trial.plan_id ? ` on ${pending.planName}` : ''}`
        : periodEndsSubscription(view, trial) ? 'Then the subscription ends and the account moves to Free'
          : `Then back to ${restorePoint(view, trial).name ?? 'their paid plan'}`;
    freeSub = trial.note ? `${then} · ${trial.note}` : then;
  }

  let pay: ReactNode;
  let paySub: ReactNode;
  if (deleting) {
    pay = 'Nothing further';
    paySub = `Billing paused · deletes ${monDY(localDay(deleteAfter!))}`;
  } else if (trial) {
    const convert = !trial.auto_convert ? ''
      : pending ? (pending.totalCents !== null ? `${money(pending.totalCents, cur)}${unit}` : '')
        : convertPrice(acct, trialPlanOf(view, trial));
    // A percent still running when the period ends comes off that first charge;
    // one that ends first does not (the old Next charge tile's rule).
    const pctAtConvert = !!pct && !pending
      && (!acct.discount_ends_at || new Date(acct.discount_ends_at).getTime() > new Date(trial.expires_at).getTime());
    pay = convert ? `${convert}${pctAtConvert ? ` less ${pct}%` : ''}` : 'Nothing';
    paySub = convert ? `From ${monDY(trial.expires_at)} · $0 until then` : `Free until ${monDY(trial.expires_at)}`;
  } else if (pays) {
    pay = `${money(pays, cur)}${unit}`;
    paySub = `MRR ${money(mrr.cents, cur)}`;
  } else {
    pay = 'Nothing';
    paySub = acct.subscription_status ? 'No charge' : 'No subscription';
  }

  const planName = pending ? pending.planName : plan.name;
  return (
    <div className="pl-ao-rows">
      <Row label="Plan" sub={billing?.planSub ?? (list ? `Billed ${interval === 'year' ? 'annually' : 'monthly'}` : 'No charge')}>
        <b>{planName}{list ? ` · ${money(list, cur)}${unit} list` : ''}</b>
      </Row>
      {billing?.afterPlan}
      <Row label="Pricing" sub={pricingSub} tone={agreed || pct ? undefined : 'muted'}
        actions={billing?.pricingActions} extra={billing?.pricingExtra}>
        {pricing}
      </Row>
      <Row label="Free period" sub={freeSub} tone={trial ? 'info' : 'muted'}
        actions={billing?.freeActions} extra={billing?.freeExtra}>
        {trial ? `${trial.plan_name} until ${monDY(trial.expires_at)}` : 'None'}
      </Row>
      <Row label="They pay" sub={paySub}><b>{pay}</b></Row>
      {billing?.after}
    </div>
  );
}
